import { DataTable, DataTablePagination } from 'components/common'
import { columns } from './utils'
import { useLocation, useNavigate } from 'react-router-dom'
import { RowClickedEvent } from '@ag-grid-community/core'
import { PAGE_MASTER_DATA_UPDATE } from 'utils/constants'
import { memo, useEffect, useState } from 'react'
import { useQuery } from 'hooks'
import { fetchMasterDataDetails } from 'utils/api/masterDataApi'
import { filterEmptyValues } from 'utils/helpers'
import { SetSelectedColumns } from 'types/dataTable'
import toast from 'react-hot-toast'
import {
  BARCODE_COUNT_KEY,
  BARCODE_ERROR_MESSAGES,
  BARCODE_QUERY_KEY,
  BARCODE_SEARCH_EXPIRED,
  BARCODE_TOKEN_KEY,
  getBarcodeErrorCode,
} from './masterDataBarcodeSearch'

type Props = {
  isEditor: boolean
  setSelectedColumns: SetSelectedColumns
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

const MasterDataTable = ({
  isEditor,
  setSelectedColumns,
  loading,
  setLoading,
}: Props) => {
  const [masterDataResponse, setMasterDataResponse] = useState<any>({})

  const navigate = useNavigate()
  const { search } = useLocation()
  const { getAllQueries, setMultipleQueries } = useQuery()

  const getMasterData = async () => {
    setLoading(true)
    const queries = getAllQueries()
    const { limit, page, search, masterDataFilters, barcodeToken } = queries
    const barcode = queries[BARCODE_QUERY_KEY]

    // removing empty values and keys
    const filters = filterEmptyValues(masterDataFilters)

    try {
      const { data } = await fetchMasterDataDetails({
        limit: limit || 10,
        page: page || 1,
        search: search,
        filters: filters,
        barcodeToken: barcodeToken,
        barcode: barcode,
      })
      setSelectedColumns(data?.tableConfig?.master || [])

      setMasterDataResponse({ ...data })
    } catch (error) {
      // Barcode search token expired → clear it (triggers a normal refetch)
      // and prompt the user to upload the file again.
      if (getBarcodeErrorCode(error) === BARCODE_SEARCH_EXPIRED) {
        setMultipleQueries({
          [BARCODE_TOKEN_KEY]: undefined,
          [BARCODE_COUNT_KEY]: undefined,
          page: 1,
        })
        toast.error(BARCODE_ERROR_MESSAGES[BARCODE_SEARCH_EXPIRED], {
          duration: 4000,
        })
      }
      setMasterDataResponse({ docs: [] })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getMasterData()
  }, [search])

  const onRowClicked = (event: RowClickedEvent) => {
    if (!isEditor) return
    navigate(`${PAGE_MASTER_DATA_UPDATE.path}/${event.data._id}`)
  }

  return (
    <>
      <DataTable
        columns={loading ? [] : columns}
        rows={loading ? null : masterDataResponse?.docs}
        identifier="_id"
        onRowClicked={isEditor ? onRowClicked : () => {}}
        activeColumnsKey="master"
        activeColumns={masterDataResponse?.tableConfig?.master || []}
      />

      {/* Table Pagination */}
      <DataTablePagination
        currentPage={masterDataResponse?.page}
        totalPages={masterDataResponse?.totalPages}
        hasNextPage={masterDataResponse?.hasNextPage}
        hasPrevPage={masterDataResponse?.hasPrevPage}
        limit={masterDataResponse?.limit}
        nextPage={masterDataResponse?.nextPage}
        prevPage={masterDataResponse?.prevPage}
        disabled={loading}
      />
    </>
  )
}

export default memo(MasterDataTable)
